import type { ContactResult } from "./types";
import { supabase } from "@/lib/supabase";

export type ContactsPathAwareResult =
  | {
      ok: true;
      results: ContactResult[];
      generalFallback?: {
        source: "user" | "external" | null;
        sourceId: string | null;
      };
      debug?: {
        effectiveTrustLevel?: number;
        computedTrustLevel?: number;
        requestedTrustOverride?: number | null;
        appliedTrustOverride?: boolean;
        canOverrideTrust?: boolean;
      };
    }
  | { ok: false; error: string; results: ContactResult[] };

type ContactsPathAwareDebug = {
  effectiveTrustLevel?: number;
  computedTrustLevel?: number;
  requestedTrustOverride?: number | null;
  appliedTrustOverride?: boolean;
  canOverrideTrust?: boolean;
};

export async function fetchContactsPathAware(args: {
  q: string;
  limit?: number;
  organizationId?: string | null;
  locationId?: string | null;
  trustLevelOverride?: number | null;
}): Promise<ContactsPathAwareResult> {
  const q = args.q || "";
  const limit = args.limit ?? 8;
  const organizationId = args.organizationId ?? null;
  const locationId = args.locationId ?? null;
  const trustLevelOverride = args.trustLevelOverride ?? null;

  if (!organizationId || !supabase) {
    return { ok: false, error: "organization context unavailable for path contacts", results: [] };
  }

  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) return { ok: false, error: "missing auth session for path contacts", results: [] };

    const resp = await fetch("/api/contacts/path-search", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ q, limit, organizationId, locationId, trustLevelOverride }),
    });

    const json = await resp.json().catch(() => null);
    if (!resp.ok || !json?.ok) {
      return { ok: false, error: (json as { error?: string } | null)?.error || "path contacts request failed", results: [] };
    }
    const rows = (json.results || []) as ContactResult[];
    return {
      ok: true,
      results: rows,
      generalFallback: (json.generalFallback || undefined) as
        | { source: "user" | "external" | null; sourceId: string | null }
        | undefined,
      debug: (json.debug || undefined) as ContactsPathAwareDebug | undefined,
    };
  } catch {
    return { ok: false, error: "path contacts request failed", results: [] };
  }
}

export async function logContactRoutingSelection(args: {
  organizationId?: string | null;
  query: string;
  pathKey?: string | null;
  chosenContactId?: string | null;
  chosenContactSource?: "user" | "external" | null;
  recommendedContactId?: string | null;
  recommendedContactSource?: "user" | "external" | null;
  overrideReason?: string | null;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  const organizationId = args.organizationId ?? null;
  if (!organizationId || !supabase) {
    return { ok: false, error: "organization context unavailable for routing event" };
  }
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) return { ok: false, error: "missing auth session for routing event" };

    const resp = await fetch("/api/contacts/path-select", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        organizationId,
        query: args.query || "",
        pathKey: args.pathKey || null,
        chosenContactId: args.chosenContactId || null,
        chosenContactSource: args.chosenContactSource || null,
        recommendedContactId: args.recommendedContactId || null,
        recommendedContactSource: args.recommendedContactSource || null,
        overrideReason: args.overrideReason || null,
      }),
    });
    const json = await resp.json().catch(() => null);
    if (!resp.ok || !json?.ok) {
      return { ok: false, error: (json as { error?: string } | null)?.error || "routing event request failed" };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: "routing event request failed" };
  }
}
